// src/components/ActiveInactiveVehiclesCarousel.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  CartesianGrid,
  Legend,
} from "recharts";

// Single colors for all Active and Inactive bars
const ACTIVE_COLOR = "#16a34a"; // green
const INACTIVE_COLOR = "#f59e0b"; // orange

export default function ActiveInactiveVehiclesCarousel({
  misData,
  autoDelay = 15000,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // City keys from your API
  const cities = ["hyderabad", "bgl_ohm", "mgl_ohm"];
  const cityNames = ["Hyderabad", "Bangalore", "Mangalore"];
  const variants = ["byd", "mg", "citroen"];

  // Prepare data for each city
  const allCityData = cities.map((cityKey, i) => {
    const cityInfo = misData?.info?.[cityKey] || {};
    const cityData = { name: cityNames[i] };

    variants.forEach((variant) => {
      cityData[`Active_${variant}`] = cityInfo?.active_vehicles?.[variant] || 0;
      cityData[`Inactive_${variant}`] =
        cityInfo?.inactive_vehicles?.[variant] || 0;
    });

    return cityData;
  });

  const cityCount = allCityData.length;

  // Auto rotate carousel
  useEffect(() => {
    if (!cityCount) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cityCount, autoDelay]);

  const handleDotClick = (idx) => {
    setCurrentIndex(idx);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);
  };

  if (!misData?.info || cityCount === 0) return null;

  const city = allCityData[currentIndex];

  // Render label: show variant name and value on top
  const renderLabel = (props, variant) => {
    const { value, x, y, width } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#111"
        textAnchor="middle"
        fontSize={12}
        fontWeight="bold"
      >
        {`${variant.toUpperCase()}: ${value}`}
      </text>
    );
  };

  return (
    <div className="flex flex-col items-center w-full h-full">
      <div className="bg-white shadow-md w-full h-full flex flex-col">
        <h2 className="text-lg font-bold text-gray-800 mb-3 text-center">
          {city.name} Vehicles
        </h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={[city]}
            // margin={{ top: 50, right: 30, left: 20, bottom: 20 }}
            barGap={25} // increased space between bars inside a group
            barCategoryGap="70%" // increased spacing between categories (though only 1 city at a time)
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={false} axisLine={false} />
            <YAxis />
            <Legend />

            {/* Active bars */}
            {variants.map((variant) => (
              <Bar
                key={`Active_${variant}`}
                dataKey={`Active_${variant}`}
                fill={ACTIVE_COLOR}
                barSize={35}
              >
                <LabelList
                  dataKey={`Active_${variant}`}
                  content={(props) => renderLabel(props, variant)}
                />
              </Bar>
            ))}

            {/* Inactive bars */}
            {variants.map((variant) => (
              <Bar
                key={`Inactive_${variant}`}
                dataKey={`Inactive_${variant}`}
                fill={INACTIVE_COLOR}
                barSize={35}
              >
                <LabelList
                  dataKey={`Inactive_${variant}`}
                  content={(props) => renderLabel(props, variant)}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Carousel dots */}
      <div className="flex mt-6 space-x-2">
        {allCityData.map((_, idx) => (
          <button
            key={idx}
            onClick={() => handleDotClick(idx)}
            className={`w-3 h-3 rounded-full transition-all ${
              idx === currentIndex ? "bg-blue-600 w-4 h-4" : "bg-gray-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
